import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import DialogTitle from 'components/DialogOverrides/DialogTitle';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  compareLabourRoleRatesImport,
  compareLabourRoleRatesUpdateImport,
  bulkUpdateLabourRoleRates,
  downloadLabourRoleRatesNewTemplate,
  downloadLabourRoleRatesUpdateTemplate,
  hasExistingRoleRates,
  BulkUpdateRoleRatesResponse,
  ImportMode,
} from 'api/labourApi';
import { handleMutationError } from 'util/handleMutationError';
import BulkUpdateSimpleRatesForm from './BulkUpdateSimpleRatesForm';
import { RoleRatesResultView } from './LabourImportResultView';

interface Props {
  open: boolean;
  handleClose: () => void;
}

const TAB_LABEL: Record<ImportMode, string> = {
  new: 'Import New Default Role Rates',
  update: 'Update Existing Default Role Rates',
};

const TEMPLATE_FILENAME: Record<ImportMode, string> = {
  new: 'restorize_labour_rates_role_new_template.xlsx',
  update: 'restorize_labour_rates_role_update_template.xlsx',
};

const DESCRIPTION_TEXT: Record<ImportMode, string> = {
  new: 'Upload an .xlsx file to set the default Cost Rate, Billing Rate, and their Working Away From Home counterparts for each role.',
  update: "Download a template pre-filled with each role's current default rates, edit the values you want to change, then upload it back.",
};

const INFO_TEXT: Record<ImportMode, string> = {
  new: "In the downloaded template, pick a Role from the dropdown, then enter Cost Rate, Billing Rate, and their Working Away From Home counterparts. Cost Rate and Working Away From Home Cost Rate share one Effective Date; Billing Rate and Working Away From Home Billing Rate share another (leave either date blank to use today's date, or enter one in YYYY-MM-DD format). If the role already has an active rate, you can leave one half of a pair blank to keep its current value. If it doesn't, both values in that pair are required. All changes are presented for review and are not saved until confirmed.",
  update: "The template is pre-filled with all 4 roles. The Role, Current Cost Rate, Current Working Away From Home Cost Rate, Current Cost Rate Effective From Date, Current Billing Rate, Current Working Away From Home Billing Rate, and Current Billing Rate Effective From Date columns are read-only for reference. Enter a value in New Cost Rate and/or New Billing Rate (and their Working Away From Home counterparts) to update it, with its matching New Effective From Date (use YYYY-MM-DD format, leave blank to use today's date). If a role has no active rate yet, both values in that pair are required. All changes are presented for review and are not saved until confirmed.",
};

const RoleRatesTab = ({ mode, onBusyChange, onClose }: { mode: ImportMode; onBusyChange: (busy: boolean) => void; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkUpdateRoleRatesResponse | null>(null);
  const [result, setResult] = useState<BulkUpdateRoleRatesResponse | null>(null);
  const { data: hasExistingRates } = useQuery(['role-rates-exist'], hasExistingRoleRates, {
    enabled: mode === 'update',
  });
  const noExistingRoleData = mode === 'update' && hasExistingRates === false;

  const { mutate: previewMutate, isLoading: isPreviewLoading } = useMutation(
    mode === 'update' ? compareLabourRoleRatesUpdateImport : compareLabourRoleRatesImport,
    {
      onSuccess: (data) => setPreview(data),
      onError: (error) => {
        enqueueSnackbar(`Failed to read the file: ${handleMutationError(error)}`, { variant: 'error' });
      },
    }
  );

  const { mutate: commitMutate, isLoading: isCommitLoading } = useMutation(bulkUpdateLabourRoleRates, {
    onSuccess: async (data) => {
      await queryClient.invalidateQueries(['labour-rates']);
      setResult(data);
      enqueueSnackbar(
        data.errorCount === 0
          ? 'Labour role rates updated successfully'
          : `Labour role rates updated — ${data.errorCount} row(s) skipped`,
        { variant: 'success' }
      );
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update labour role rates: ${handleMutationError(error)}`, {
        variant: 'error',
      });
    },
  });

  const { mutate: downloadMutate, isLoading: isDownloadingTemplate } = useMutation(
    mode === 'update' ? downloadLabourRoleRatesUpdateTemplate : downloadLabourRoleRatesNewTemplate,
    {
      onError: (error) => {
        enqueueSnackbar(`Failed to download the template: ${handleMutationError(error)}`, {
          variant: 'error',
        });
      },
    }
  );

  const isBusy = isPreviewLoading || isCommitLoading || isDownloadingTemplate;
  React.useEffect(() => onBusyChange(isBusy), [isBusy, onBusyChange]);

  const step = result ? 'result' : preview ? 'preview' : 'form';

  const resultDivergedFromPreview =
    !!preview &&
    !!result &&
    (preview.createdCount !== result.createdCount ||
      preview.updatedCount !== result.updatedCount ||
      preview.ignoredCount !== result.ignoredCount ||
      preview.errorCount !== result.errorCount);

  return (
    <>
      {step === 'form' && (
        <>
          <DialogContent sx={{ p: 0 }}>
            <BulkUpdateSimpleRatesForm
              file={file}
              setFile={setFile}
              isLoading={isPreviewLoading}
              descriptionText={DESCRIPTION_TEXT[mode]}
              infoText={INFO_TEXT[mode]}
              templateFilename={TEMPLATE_FILENAME[mode]}
              onDownloadTemplate={() => downloadMutate()}
              isDownloadingTemplate={isDownloadingTemplate}
              blockedMessage={
                noExistingRoleData
                  ? 'Your organization doesn\'t have any existing default role rates yet. Use the "Import New Default Role Rates" tab to add some first.'
                  : undefined
              }
            />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button onClick={onClose} sx={{ color: colors.grey[600], mr: 1 }} disabled={isPreviewLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isPreviewLoading || !file}
                endIcon={isPreviewLoading ? <CircularProgress size={15} /> : <ArrowForwardIcon />}
                onClick={() => {
                  if (!file) return;
                  previewMutate({ file });
                }}>
                {isPreviewLoading ? 'Please wait...' : 'Preview'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'preview' && preview && (
        <>
          <RoleRatesResultView response={preview} />
          <Divider />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button onClick={() => setPreview(null)} sx={{ color: colors.grey[600], mr: 1 }} disabled={isCommitLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={
                  isCommitLoading ||
                  !preview.previewToken ||
                  (preview.createdCount === 0 && preview.updatedCount === 0)
                }
                endIcon={isCommitLoading ? <CircularProgress size={15} /> : <CheckIcon />}
                onClick={() => {
                  if (!preview.previewToken) return;
                  commitMutate({ previewToken: preview.previewToken });
                }}>
                {isCommitLoading
                  ? 'Please wait...'
                  : preview.errorCount > 0
                    ? 'Ignore Errors and Continue'
                    : 'Confirm Import'}
              </Button>
            </Box>
          </DialogActions>
        </>
      )}

      {step === 'result' && result && (
        <>
          {resultDivergedFromPreview && (
            <Alert severity="warning" sx={{ m: 2 }}>
              The committed result differs from the preview you approved, data may have changed since
              preview. Review the details below.
            </Alert>
          )}
          <RoleRatesResultView response={result} />
          <DialogActions>
            <Box mr={3} mt={1} mb={2}>
              <Button variant="contained" color="primary" endIcon={<CheckIcon />} onClick={onClose}>
                Done
              </Button>
            </Box>
          </DialogActions>
        </>
      )}
    </>
  );
};

const BulkUpdateRoleRatesDialog = ({ open, handleClose }: Props) => {
  const [isBusy, setIsBusy] = useState(false);
  const [mode, setMode] = useState<ImportMode>('new');

  const onClose = () => {
    if (isBusy) return;
    handleClose();
  };

  return (
    <Dialog open={open} onClose={onClose} disableEnforceFocus fullWidth scroll="paper" maxWidth="lg">
      <DialogTitle onClose={onClose}>Import Default Rates by Role</DialogTitle>
      <Divider />
      <Tabs
        value={mode}
        onChange={(_e, value) => {
          if (isBusy) return;
          setMode(value);
        }}
        variant="fullWidth">
        <Tab label={TAB_LABEL.new} value="new" disabled={isBusy} />
        <Tab label={TAB_LABEL.update} value="update" disabled={isBusy} />
      </Tabs>
      <Divider />

      <RoleRatesTab key={mode} mode={mode} onBusyChange={setIsBusy} onClose={onClose} />
    </Dialog>
  );
};

export default BulkUpdateRoleRatesDialog;
